import { useCase } from "../../../provider/CaseProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createResource, fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import _ from "lodash";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { getEmailSignature } from "../../../services/Template/emailSignatures";
import { FormContainer, MultiSelectElement, TextFieldElement, useForm } from "react-hook-form-mui";
import { Autocomplete, Box, Button, Grid, IconButton, TextField, Tooltip } from "@mui/material";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import React, { useEffect, useMemo, useState } from "react";
import { isEmail } from "../../../services/validationRules";
import { FileCopyOutlined, SettingsBackupRestore } from "@mui/icons-material";
import ButtonLoading from "../../Button/ButtonLoading";
import { MediaObject } from "../../../types/MediaObject";
import { apiPost } from "../../../services/Api/apiCall";
import { loadSessionStorageState, saveSessionStorageState } from "../../../services/browserStorageService";
import { Channel } from "../../../types/Messaging/Channel";
import MenuItem from "@mui/material/MenuItem";
import { fillTemplate, matchesTemplateRecipientType } from "../../../services/Template/templateService";
import { getCourtRepresentative } from "../../../services/Product/ProductService/productService";
import { isLitigationCase } from "../../../types/AbstractCase";
import { AutoCompleteOptionWithId } from "../../../types/AutoCompleteOptionWithId";
import Template from "../../../types/Template";
import { translate } from "../../../services/Translations/translatorService";
import { Controller, FieldValues } from "react-hook-form";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import AddTextBlock from "../../LbEditor/Toolbar/AddTextBlock";
import LegalbirdTipTapEditorProvider from "../../LbEditor/LegalbirdTipTapEditorProvider";
import { Editor, useCurrentEditor } from "@tiptap/react";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { UseForm } from "../../../types/UseForm";
import { Person } from "../../../types/Person";
import { getMediaObjectFormattedDescription, getRenderValue } from "../../../services/mediaObjectService";
import AiWizard from "../../LbEditor/Toolbar/AiWizard/AiWizard";
import { userHasRole } from "../../../services/backofficeUserService";
import { Roles } from "../../../types/BackofficeUser";
import { ProductUnion } from "../../../types/ProductUnion";
import { createPortal } from "react-dom";
import { getOrganizationRecipient } from "../../../services/documentRecipientService";
import { CaseOrganization } from "../../../types/CaseOrganization";
import { Message } from "../../../types/Messaging/Message";
import { getIriOfSubEntity } from "../../../services/utils";
import { useDrawer } from "../../../provider/DrawerProvider";
import useDraftAutosave from "../../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../../GlobalDrawer/DraftAutosaveIndicator";
import { SendEmailExecutionStepPayload } from "../../../types/ExecutionJob/SendEmailExecutionStepPayload.ts";
import { useSnackbar } from "notistack";
import { type StarterKitOptions } from "@tiptap/starter-kit";

const messageStarterKitOptions = {
  blockquote: false,
  code: false,
  codeBlock: false,
  heading: false,
  horizontalRule: false,
  strike: false,
} satisfies Partial<StarterKitOptions>;

type ComposeEmailOrMessageFormProps = {
  product: ProductUnion;
  closeDialog: () => void;
  customerCommunicationOnly?: boolean;
  //locks the form to email mode: a sendEmail execution step payload must stay email-shaped, so message-mode recipients are hidden
  emailCommunicationOnly?: boolean;
  message?: Message;
  emailDraft?: SendEmailExecutionStepPayload;
  overwrittenHandleSave?: Function;
};

export default function ComposeEmailOrMessageForm(props: ComposeEmailOrMessageFormProps) {
  return (
    <LegalbirdTipTapEditorProvider
      starterKitOptions={props.emailCommunicationOnly ? {} : messageStarterKitOptions}
      onUpdate={({ editor }: { editor: Editor }) => saveEditorBackup(getEditorHtml(editor))}
    >
      <ComposeEmailOrMessageFormInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
}

function ComposeEmailOrMessageFormInner({
  closeDialog,
  customerCommunicationOnly,
  emailCommunicationOnly,
  message,
  emailDraft,
  overwrittenHandleSave,
}: ComposeEmailOrMessageFormProps) {
  const { product } = useCase();
  const { editor } = useCurrentEditor();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [carbonCopy, setCarbonCopy] = useState(Boolean(emailDraft?.cc));
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const attachmentsFileLimitReached = false;
  const { drawerActionsNode, drawerHeaderNode } = useDrawer();
  const mediaObjectsFilter = {
    product: product?.productClassName,
    productId: product?.id,
  };
  const { data: mediaObjectsCollection } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product,
  });

  const templateFilter = {
    type: "email",
  };

  const { data: templatesCollection } = useQuery({
    queryKey: queryKeys.collection("mailTemplates", templateFilter),

    queryFn: () => fetchCollection("templates", templateFilter),
  });

  //sendCustomerMessage execution step payloads carry attachment ids, saved user drafts and real messages carry MediaObject entities
  const messageAttachmentIds = ((message?.attachments ?? []) as Array<MediaObject | number>).map((attachment) =>
    typeof attachment === "number" ? attachment : attachment.id,
  );

  let defaultRecipientType = emailCommunicationOnly ? "custom" : "customer";
  if (emailDraft) {
    defaultRecipientType = emailDraft.recipientType ?? "";
  }

  const formContext = useForm({
    mode: "onTouched",
    defaultValues: {
      recipient: emailDraft ? (emailDraft.toMail ?? "") : product?.customer?.email || "",
      subject:
        emailDraft?.subject ||
        (typeof message?.conversation === "object"
          ? message.conversation.subject
          : " (Unser Zeichen: " + product?.reference + ")"),
      chosenTemplate: { id: "no_template", label: "Leerer Entwurf" },
      recipientType: defaultRecipientType,
      carbonCopyType: "customer",
      attachedMediaObjects: emailDraft?.mediaObjectIds || messageAttachmentIds,
      carbonCopyRecipient: emailDraft?.cc || product?.customer?.email || "",
    },
  });
  const { control } = useForm();

  const setEditorContent = (content: string) => {
    if (!editor) {
      return;
    }
    editor?.commands.setContent(content);
    editor?.commands.focus("start");
  };

  useEffect(() => {
    if (emailDraft?.html) {
      setEditorContent(emailDraft.html);
      return;
    }

    if (message?.messageText) {
      setEditorContent(message.messageText);
      return;
    }

    setEditorContent("<p></p>" + getEmailSignature(currentUser, product, true));
  }, [editor]);

  useEffect(() => {
    saveFormValuesBackup(formContext.getValues());
  }, [formContext.watch()]);

  //the CC select keeps the full list; only the main recipient select must exclude message-mode recipients
  const recipientMenuItems = useMemo(() => getRecipientMenuItems(product), [product]);
  const recipientTypeMenuItems = useMemo(
    () => getRecipientMenuItems(product, emailCommunicationOnly),
    [product, emailCommunicationOnly],
  );
  const emailTemplateOptions = useMemo(
    () => getEmailTemplateOptions(templatesCollection, product, formContext.watch("recipientType")),
    [templatesCollection, product, formContext.watch("recipientType")],
  );

  const conversationIri = message ? getIriOfSubEntity(message, "conversation") : null;
  const mediaObjects = _.get(mediaObjectsCollection, "hydra:member");

  const isComposeMessage = Boolean(
    !emailCommunicationOnly &&
    (formContext.watch("recipientType") === "customer" ||
      (formContext.watch("recipientType") === "insurance" && product?.eConsultDossierId)),
  );
  const isComposeEmail = !isComposeMessage;

  const buildMailData = (values: Record<string, any>) => {
    let mailData: any = {
      toMail: values.recipient,
      recipientType: values.recipientType,
      productClassName: product?.productClassName,
      productId: product?.id,
      subject: values.subject,
      html: editor ? getEditorHtml(editor) : "",
      mediaObjectIds: values.attachedMediaObjects,
    };
    if (carbonCopy && !!values.carbonCopyRecipient) {
      mailData.cc = values.carbonCopyRecipient;
    }
    return mailData;
  };

  const fetchCaseChannels = async (): Promise<Channel[]> =>
    (await fetchCollection("channels", { case: product?.id }))["hydra:member"];

  const buildMessageData = async (values: Record<string, any>): Promise<Message> => {
    const attachments = values.attachedMediaObjects.map((mediaObjectId: number) => {
      const mediaObject = mediaObjects.find((mediaObject: MediaObject) => mediaObject.id === mediaObjectId);
      //prefilled payload ids may reference a media object missing from the collection; the backend accepts the id shape
      return mediaObject ? { ...mediaObject, displayUrl: null, downloadUrl: null } : { id: mediaObjectId };
    });

    const messageText = editor ? getEditorHtml(editor) : "";
    const messageAttachments = attachments.length > 0 ? attachments : null;
    let messageData: Message = message ? { ...message } : { messageText, attachments: messageAttachments };

    //todo apply style in a better way
    messageData.messageText = '<div style="color: #272D4E;">' + messageText + "</div>";
    messageData.attachments = messageAttachments;

    if (conversationIri) {
      messageData.conversation = conversationIri;
    } else {
      const caseChannels = await fetchCaseChannels();
      let channelIri;

      switch (formContext.watch("recipientType")) {
        case "insurance":
        case "opposingCarLiabilityInsurance":
          channelIri = caseChannels.find((channel: Channel) => channel.type === "internalPlusLegalExpenseInsurance");
          break;
        default:
          channelIri = caseChannels.find((channel: Channel) => channel.type === "all");
          break;
      }

      if (!channelIri) {
        throw new Error("channel not found");
      }

      messageData.conversation = {
        subject: values.subject,
        type: "submitCaseInfo",
        channel: channelIri,
      };
    }
    return messageData;
  };

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      const values = formContext.getValues();
      if (isComposeEmail) {
        await overwrittenHandleSave?.(buildMailData(values));
      } else {
        await overwrittenHandleSave?.(await buildMessageData(values));
      }
    },
    editor,
  });

  useEffect(() => {
    if (!overwrittenHandleSave) {
      return;
    }
    const subscription = formContext.watch(() => scheduleSave());
    return () => subscription.unsubscribe();
  }, []);

  useEffectWithoutMount(() => scheduleSave(), [carbonCopy]);

  if (!product || !mediaObjectsCollection || !templatesCollection || !editor || !drawerActionsNode) {
    return null;
  }

  const templates = _.get(templatesCollection, "hydra:member");

  const handleChosenTemplateChange = (event: React.SyntheticEvent, chosenTemplate: { id: string; label: string }) => {
    if (!product) {
      return;
    }
    const { subject, htmlContent } = getTemplateData(
      chosenTemplate.id,
      templates,
      product,
      formContext.watch(),
      getEmailSignature(currentUser, product, isComposeMessage),
    );
    formContext.setValue("subject", subject);
    formContext.setValue("chosenTemplate", chosenTemplate);
    setEditorContent(htmlContent);
  };

  const bccMail = `legalbird-rechtsanwaelte-bcc+${product.productClassName}${product.id}@legalbird.de`;

  const handleSubmit = (values: any) => {
    //draft mode must never send anything; persistence runs through the autosave (see EditActivity for the discussion)
    if (overwrittenHandleSave) {
      return;
    }
    if (isComposeEmail) {
      handleMailSubmit(values);
    }
    if (isComposeMessage) {
      handleMessageSubmit(values);
    }
  };

  const handleMailSubmit = async (values: Record<string, any>) => {
    setIsLoading(true);
    const mailData = buildMailData(values);


    apiPost("email/email_send", mailData).then(
      () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.collections("emails")
        });
        formContext.reset();
        setIsLoading(false);
        closeDialog();
      },
      () => {
        alert(
          "Mailversand fehlgeschlagen, sollte der Fehler nach einem Neuladen der Seite weiterhin auftreten, " +
            "kontaktieren Sie bitte die IT.",
        );
        setIsLoading(false);
      },
    );
  };
  const handleMessageSubmit = async (values: Record<string, any>) => {
    setIsLoading(true);
    let messageData: Message;
    try {
      messageData = await buildMessageData(values);
    } catch (e) {
      alert(
        "Der Channel für diesen Fall konnte nicht gefunden werden. Bitte Kontakt mit der IT aufnehmen falls das Problem weiterhin besteht.",
      );
      setIsLoading(false);
      return;
    }

    try {
      await createMutation.mutateAsync({ uri: "/messages", data: messageData });
    } catch (e) {
      enqueueSnackbar("Die Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/conversations", { "channel.case": product.id }),
    });

    formContext.reset();
    setIsLoading(false);
    closeDialog();
  };

  const handleRecipientTypeChange = (recipientType: string) => {
    formContext.setValue("recipient", getDefaultRecipientEmail(recipientType, product));
    const { subject, htmlContent } = getTemplateData(
      formContext.watch("chosenTemplate")?.id,
      templates,
      product,
      formContext.watch(),
      getEmailSignature(currentUser, product, recipientType === "customer"),
    );

    formContext.setValue("subject", subject);
    setEditorContent(htmlContent);
  };

  const loadContentFromSessionStorage = () => {
    const sessionStorageHtml = loadSessionStorageState("messageHtml");
    if (sessionStorageHtml) {
      setEditorContent(sessionStorageHtml);
    }
    const sessionStorageValues = loadSessionStorageState("messageFormValues");
    if (sessionStorageValues) {
      formContext.setValue("recipientType", sessionStorageValues.recipientType);
      formContext.setValue("recipient", sessionStorageValues.recipient);
      formContext.setValue("subject", sessionStorageValues.subject);
      formContext.setValue("chosenTemplate", sessionStorageValues.chosenTemplate);
      formContext.setValue("carbonCopyType", sessionStorageValues.carbonCopyType);
      formContext.setValue("carbonCopyRecipient", sessionStorageValues.carbonCopyRecipient);
      formContext.setValue("attachedMediaObjects", sessionStorageValues.attachedMediaObjects);
    }
  };

  const isConversationAnswer = conversationIri;

  let additionalToolbarActions = [<AddTextBlock context={"message"} />];

  if (!userHasRole(currentUser, Roles.external)) {
    additionalToolbarActions.push(
      <AiWizard
        product={product}
        componentScopes={{
          customMessage: {
            name: "Keine Vorlage",
            scope: "customMessage",
            isEnabled: (product: ProductUnion) =>
              [
                "generalCivilLaw",
                "employmentContract",
                "civilServiceLaw",
                "rentalContract",
                "divorce",
                "trafficAccident",
                "settlement",
                "traffic",
              ].includes(product.productClassName) || product.productCategory === "contractLaw",
          },
        }}
        defaultScopeKey={"customMessage"}
        promptContext={{
          recipientType: formContext.watch("recipientType"),
          conversationId: conversationIri ? Number(conversationIri.split("/").pop()) : null,
        }}
      />,
    );
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <FormContainer formContext={formContext}>
          <Grid
            container
            sx={{
              alignContent: "center",
            }}
          >
            <Grid sx={{ paddingRight: "1rem" }} size={6}>
              <SelectElement
                disabled={!!isConversationAnswer || customerCommunicationOnly}
                label={"Empfänger"}
                name={"recipientType"}
                validation={{ required: "Dies ist ein Pflichtfeld" }}
                onChange={handleRecipientTypeChange}
              >
                {recipientTypeMenuItems}
              </SelectElement>
            </Grid>
            {isComposeEmail && (
              <Grid sx={{ paddingLeft: "1rem" }} size={6}>
                <TextFieldElement
                  label={"Email-Adresse Empfänger"}
                  name={"recipient"}
                  //no frontend validation for step-edit drafts: the backend executors validate at execution time (dev_pm decision)
                  rules={
                    overwrittenHandleSave
                      ? undefined
                      : {
                          required: "Die Email-Adresse des Empfängers ist ein Pflichtfeld",
                          validate: {
                            isEmail: (value: string) => isEmail(value) || "Dies ist keine gültige E-Mail Adresse",
                          },
                        }
                  }
                />
              </Grid>
            )}
            <Grid sx={isComposeEmail ? { paddingRight: "1rem" } : {}} size={6}>
              <Controller
                control={control}
                name={"chosenTemplate"}
                render={({ field: { onBlur } }) => (
                  <Autocomplete
                    disableClearable
                    renderInput={(params) => <TextField {...params} label="Nachrichtenvorlage" />}
                    options={emailTemplateOptions}
                    onChange={handleChosenTemplateChange}
                    onBlur={onBlur}
                    value={formContext.getValues("chosenTemplate")}
                    isOptionEqualToValue={(option, value) => {
                      return option.id === value.id;
                    }}
                  />
                )}
              />
            </Grid>
            {isComposeEmail && (
              <Grid sx={{ paddingLeft: "1rem" }} size={6}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                    columnGap: "1rem",
                    height: "100%",
                    padding: "1rem 0",
                  }}
                >
                  <Button onClick={() => setCarbonCopy(!carbonCopy)} sx={{ whiteSpace: "nowrap" }}>
                    {carbonCopy ? "CC entfernen" : "CC hinzufügen"}
                  </Button>
                  <Button onClick={() => copyBccMailToClipboard(bccMail)} endIcon={<FileCopyOutlined />}>
                    BCC
                  </Button>
                </Box>
              </Grid>
            )}
            {carbonCopy && (
              <>
                <Grid sx={{ paddingRight: "1rem" }} size={6}>
                  <SelectElement
                    label={"CC Empfänger"}
                    name={"carbonCopyType"}
                    onChange={(carbonCopyType: any) =>
                      formContext.setValue("carbonCopyRecipient", getDefaultRecipientEmail(carbonCopyType, product))
                    }
                  >
                    {recipientMenuItems}
                  </SelectElement>
                </Grid>
                <Grid sx={{ paddingLeft: "1rem" }} size={6}>
                  <TextFieldElement
                    label={"CC E-Mail-Adresse"}
                    name={"carbonCopyRecipient"}
                    slotProps={{
                      input: {
                        readOnly: formContext.watch("carbonCopyType") === "customer",
                      },
                    }}
                    rules={
                      overwrittenHandleSave
                        ? undefined
                        : {
                            validate: {
                              isEmail: (value: string) => isEmail(value) || "Dies ist keine gültige E-Mail Adresse",
                            },
                          }
                    }
                  />
                </Grid>
              </>
            )}
            {!isConversationAnswer && (
              <Grid size={12}>
                <TextFieldElement
                  label={"Betreff"}
                  name={"subject"}
                  rules={overwrittenHandleSave ? undefined : { required: "Betreff ist ein Pflichtfeld" }}
                />
              </Grid>
            )}
            <Grid
              sx={{
                marginTop: "1rem",
                minHeight: "37vh",
              }}
              size={12}
            >
              <LegalbirdTipTapEditor additionalToolbarActions={additionalToolbarActions} />
            </Grid>
            {mediaObjects && (
              <Grid
                sx={{
                  padding: "1rem 0",
                  display: "grid",
                }}
                size={12}
              >
                <MultiSelectElement
                  fullWidth
                  label={"Datei anhängen"}
                  name={"attachedMediaObjects"}
                  options={_.map(mediaObjects, (mediaObject) => ({
                    id: mediaObject.id,
                    label: getMediaObjectFormattedDescription(mediaObject),
                  }))}
                  showCheckbox={true}
                  renderValue={(mediaObjectIds: any) => getRenderValue(mediaObjectIds, mediaObjects)}
                />
              </Grid>
            )}
          </Grid>
        </FormContainer>
      </Box>
      {overwrittenHandleSave
        ? drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)
        : createPortal(
            <Grid container sx={{ width: "100%" }}>
              <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
              <Grid size={{ xs: 12, md: 6 }} sx={{ alignContent: "center" }}>
                <ButtonLoading
                  variant={"contained"}
                  isLoading={isLoading}
                  disabled={attachmentsFileLimitReached}
                  onClick={formContext.handleSubmit(handleSubmit)}
                >
                  Nachricht senden
                </ButtonLoading>
              </Grid>
              <Grid size={3}>
                <Tooltip title={"Inhalt wiederherstellen"}>
                  <IconButton onClick={() => loadContentFromSessionStorage()} size="large">
                    <SettingsBackupRestore />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>,
            drawerActionsNode,
          )}
    </>
  );
}

const getRecipientMenuItems = (product: ProductUnion | null, emailCommunicationOnly = false) => {
  if (!product) {
    return [];
  }

  const getContractualPartnerDisabled = () => {
    let hasOpposingPartyEmail = false;

    switch (product.opposingPartyType) {
      case "opposingPartyLegalEntity":
        const representative: Person = product.processParticipantsPeople.find(
          (person: Person) => person.labelType === "opposingPartyRepresentative",
        );
        hasOpposingPartyEmail =
          product.contractualPartnerLegalEntity.organization.mainEmail.email || representative?.email;
        break;
      case "opposingPartyPerson":
        hasOpposingPartyEmail = product.contractualPartnerPerson && product.contractualPartnerPerson.email;
        break;
      default:
        break;
    }

    return !hasOpposingPartyEmail;
  };

  let menuItems = [];
  if (!emailCommunicationOnly) {
    menuItems.push(
      <MenuItem value={"customer"} key={"customer"}>
        Mandantschaft
      </MenuItem>,
    );
  }

  //productClassName is not settlement check is added because settlement is part of employmentLaw but because it's not
  //part of litigation settlement has own employerOrganization getter, therefore, this check is needed. Remove
  //when settlement is refactored into litigationCase
  if (
    ["contractLaw", "tenancyLaw", "civilLaw", "employmentLaw"].includes(product.productCategory) &&
    product.productClassName !== "settlement"
  ) {
    menuItems.push(
      <MenuItem key={"contractualPartner"} value={"contractualPartner"} disabled={getContractualPartnerDisabled()}>
        Gegenseite
      </MenuItem>,
    );

    if (
      ["contractLaw", "tenancyLaw"].includes(product.productCategory) ||
      product.productClassName === "employmentContract"
    ) {
      menuItems.push(
        <MenuItem
          key={"debtCollectionAgency"}
          disabled={!(product.debtCollectionAgency && product.debtCollectionAgency.organizationEmail?.email)}
          value={"debtCollectionAgency"}
        >
          Inkassobüro
        </MenuItem>,
      );
    }
  }

  if (product.productClassName === "rentalContract") {
    menuItems.push(
      <MenuItem
        key={"propertyManager"}
        disabled={!product.propertyManager.organization.mainEmail.email}
        value={"propertyManager"}
      >
        Hausverwaltung
      </MenuItem>,
    );
  }

  if (
    product.productCategory === "contractLaw" ||
    ["settlement", "divorce", "rentalContract", "generalCivilLaw", "employmentContract", "civilServiceLaw"].includes(
      product.productClassName,
    )
  ) {
    menuItems.push(
      <MenuItem
        key={"opposingCounsel"}
        disabled={!(product.opponentLawFirm && product.opponentLawFirm.organization.mainEmail.email)}
        value={"opposingCounsel"}
      >
        Anwalt Gegenseite
      </MenuItem>,
    );
  }

  if (isLitigationCase(product)) {
    menuItems.push(
      <MenuItem
        disabled={!product.responsibleAuthority?.email}
        value={"responsibleAuthority"}
        key={"responsibleAuthority"}
      >
        Behörde
      </MenuItem>,
    );
  }

  if (product.productClassName === "settlement") {
    menuItems.push(
      <MenuItem value={"employerOrganization"} key={"employerOrganization"}>
        E-Mail-Adresse Gegenseite
      </MenuItem>,
    );
  }
  if (!!product.processParticipantsPeople) {
    menuItems.push(
      _.map(product.processParticipantsPeople, (person) => {
        return (
          <MenuItem key={person.id} value={"processParticipantsPeople." + person.id} disabled={!person.email}>
            {translate("participant.labelType.values." + person.labelType) +
              " - " +
              person.givenName +
              " " +
              person.familyName}
          </MenuItem>
        );
      }),
    );
  }
  if (!!product.processParticipantsOrganizations) {
    menuItems.push(
      _.map(product.processParticipantsOrganizations, (caseOrganization: CaseOrganization) => {
        return (
          <MenuItem
            key={caseOrganization.id}
            value={"processParticipantsOrganizations." + caseOrganization.id}
            disabled={!caseOrganization.organization.mainEmail?.email}
          >
            {translate("participant.labelType.values." + caseOrganization.organization.labelType) +
              " - " +
              caseOrganization.organization.legalName}
          </MenuItem>
        );
      }),
    );
  }
  if (
    (product.paymentType === "insurance" || product.productClassName === "divorce") &&
    //with an eConsult dossier the insurance recipient switches the form into message mode
    !(emailCommunicationOnly && product.eConsultDossierId)
  ) {
    menuItems.push(
      <MenuItem key={"insurance"} disabled={!Boolean(product.caseInsurance.insurer?.email)} value={"insurance"}>
        Rechtsschutzversicherung
      </MenuItem>,
    );
  }

  if (product.productClassName === "trafficAccident" && !!product.opposingCarLiabilityInsurance) {
    menuItems.push(
      <MenuItem
        key={"opposingCarLiabilityInsurance"}
        disabled={!product.opposingCarLiabilityInsurance.organization.mainEmail.email}
        value={"opposingCarLiabilityInsurance"}
      >
        KFZ-Haftpflicht Gegenseite
      </MenuItem>,
    );
  }

  if (isLitigationCase(product) && !!product.opposingLiabilityInsurance) {
    menuItems.push(
      <MenuItem
        key={"opposingLiabilityInsurance"}
        disabled={!product.opposingLiabilityInsurance.organization.mainEmail.email}
        value={"opposingLiabilityInsurance"}
      >
        Haftpflichtversicherung Gegenseite
      </MenuItem>,
    );
  }

  if (getCourtRepresentative(product)) {
    menuItems.push(
      <MenuItem key={"courtRepresentative"} value={"courtRepresentative"}>
        Terminsvertreter
      </MenuItem>,
    );
  }
  menuItems.push(
    <MenuItem value={"custom"} key={"custom"}>
      Frei auswählen
    </MenuItem>,
  );

  return menuItems;
};

const getEmailTemplateOptions = (
  templatesCollection: any,
  product: ProductUnion | null,
  selectedRecipientType: string,
): AutoCompleteOptionWithId[] => {
  if (!product || !templatesCollection) {
    return [];
  }
  const templates: Template[] = templatesCollection["hydra:member"];
  const generalTemplateList = _.filter(templates, (template) => template.productClassName === "general");
  //&& product.productClassName !== "dataLeakContract" is the solution we picked since we didnt want to make data leak its own productCategory and it actually should share the templates.
  //this is a special handling because data leak has external wbs lawyers that arnt supposed to see the general templates of contract law.
  // reconsider if data leak gets split away from contract law even more.
  const categoryTemplateList = _.filter(
    templates,
    (template) =>
      template.productClassName === product.productCategory && product.productClassName !== "dataLeakContract",
  );
  const productTemplateList = _.filter(templates, (template) => template.productClassName === product.productClassName);
  const recipientFilteredTemplates = [...generalTemplateList, ...categoryTemplateList, ...productTemplateList].filter(
    (template) =>
      selectedRecipientType === "custom" ||
      template.recipientTypes!.length === 0 || // this is for legacy templates where no recipientTypes are selected yet
      template.recipientTypes!.some((recipientType) =>
        matchesTemplateRecipientType(selectedRecipientType, template.productClassName, recipientType),
      ),
  );
  return [
    {
      id: "no_template",
      label: "Leerer Entwurf",
    },
    ...recipientFilteredTemplates.map((template) => ({ id: template.id!, label: template.label! })),
  ];
};

const copyBccMailToClipboard = async (bccMail: string) => {
  await navigator.clipboard.writeText(bccMail);
};

const getDefaultRecipientEmail = (recipientType: string, product: ProductUnion) => {
  let email = "";
  if (recipientType.startsWith("processParticipantsOrganizations")) {
    return getOrganizationRecipient(product, recipientType)?.organization.mainEmail?.email ?? "";
  }

  if (
    (isLitigationCase(product) || ["divorce", "settlement", "traffic"].includes(product.productClassName)) &&
    recipientType.startsWith("processParticipantsPeople")
  ) {
    return product.processParticipantsPeople.find(
      (participant: Person) => participant.id + "" === recipientType.split(".")[1],
    ).email;
  }

  switch (recipientType) {
    case "customer":
      email = _.get(product, "customer.email", "Nicht befüllt");
      break;
    case "contractualPartner":
      if (product.opposingPartyType === "opposingPartyLegalEntity") {
        email = _.get(product, "contractualPartnerLegalEntity.organization.mainEmail.email", "Nicht befüllt");
        break;
      }
      if (product.opposingPartyType === "opposingPartyLegalEntity") {
        email = _.get(product, "contractualPartnerLegalEntity.organization.mainEmail.email", "Nicht befüllt");
        break;
      }
      email = _.get(product, "contractualPartnerPerson.email", "Nicht befüllt");
      break;
    case "propertyManager":
      email = _.get(product, "propertyManager.organization.mainEmail.email", "Nicht befüllt");
      break;
    case "opposingCounsel":
      email = _.get(product, "opponentLawFirm.organization.mainEmail.email", "Nicht befüllt");
      break;
    case "responsibleAuthority":
      email = _.get(product, "responsibleAuthority.email", "Nicht befüllt");
      break;
    case "debtCollectionAgency":
      email = _.get(product, "debtCollectionAgency.organizationEmail.email", "Nicht befüllt");
      break;
    case "insurance":
      email = _.get(product, "caseInsurance.insurer.email", "Nicht befüllt");
      break;
    case "opposingCarLiabilityInsurance":
      email = _.get(product, "opposingCarLiabilityInsurance.organization.mainEmail.email", "Nicht befüllt");
      break;
    case "opposingLiabilityInsurance":
      email = _.get(product, "opposingLiabilityInsurance.organization.mainEmail.email", "Nicht befüllt");
      break;
    case "courtRepresentative":
      email = getCourtRepresentative(product)?.email || "";
      break;
    case "employerOrganization":
      email = _.get(product, "employerOrganization.mainEmail.email", "Nicht befüllt");
      break;
    case "custom":
      break;
    default:
      email = _.get(product, recipientType, "");
      break;
  }
  return email;
};

type TemplateData = {
  subject: string;
  htmlContent: string;
};

const getSubjectPrefix = (product: ProductUnion, recipientType: string) => {
  if (
    (isLitigationCase(product) || ["divorce", "settlement", "traffic"].includes(product.productClassName)) &&
    recipientType.startsWith("processParticipantsPeople")
  ) {
    return (
      "(Ihre Referenz: " +
      product.processParticipantsPeople.find(
        (participant: Person) => participant.id + "" === recipientType.split(".")[1],
      ).casePerson.reference +
      ") - "
    );
  }

  if (recipientType === "insurance") {
    if (product.caseInsurance.caseReferenceNumber) {
      return "Schaden-/Leistungsnummer " + product.caseInsurance.caseReferenceNumber + " - ";
    }

    if (product.caseInsurance.insurancePolicyNumber) {
      return "Versicherungsnummer " + product.caseInsurance.insurancePolicyNumber + " - ";
    }
  }

  if (product.productClassName === "trafficAccident" && recipientType === "opposingCarLiabilityInsurance") {
    if (product.opposingCaseReferenceNumber) {
      return "Schadennummer " + product.opposingCaseReferenceNumber + " - ";
    }

    if (product.opposingInsurancePolicyNumber) {
      return "Versicherungsnummer " + product.opposingInsurancePolicyNumber + " - ";
    }
  }

  if (isLitigationCase(product) && recipientType === "opposingLiabilityInsurance") {
    if (product.opposingLiabilityInsuranceCaseReferenceNumber) {
      return "Schadennummer " + product.opposingLiabilityInsuranceCaseReferenceNumber + " - ";
    }

    if (product.opposingLiabilityInsurancePolicyNumber) {
      return "Versicherungsnummer " + product.opposingLiabilityInsurancePolicyNumber + " - ";
    }
  }

  if (
    isLitigationCase(product) &&
    ["opposingParty", "contractualPartner"].includes(recipientType) &&
    product.opposingPartyReference
  ) {
    return "(Ihre Referenz: " + product.opposingPartyReference + ") - ";
  }

  return "";
};

export const getTemplateData = (
  selectedTemplateId: string | number,
  templates: Template[],
  product: ProductUnion,
  values: FieldValues,
  signature: string,
): TemplateData => {
  const subjectAddendum = "(Unser Zeichen: " + product?.reference + ")";

  if (!selectedTemplateId || selectedTemplateId === "no_template") {
    return {
      subject: getSubjectPrefix(product, values["recipientType"]) + subjectAddendum,
      htmlContent: "<p></p>" + signature,
    };
  }
  const templateToDisplay = _.find(templates, (template) => template.id === selectedTemplateId);
  if (!templateToDisplay) {
    return {
      subject: getSubjectPrefix(product, values["recipientType"]) + subjectAddendum,
      htmlContent: "<p></p>" + signature,
    };
  }

  return {
    subject:
      getSubjectPrefix(product, values["recipientType"]) +
      fillTemplate(templateToDisplay.subject, product, values) +
      " " +
      subjectAddendum,
    htmlContent: fillTemplate(templateToDisplay.content, product, values) + signature,
  };
};

const saveEditorBackup = _.debounce((htmlContent: string | null) => {
  saveSessionStorageState("messageHtml", htmlContent);
}, 3000);

const saveFormValuesBackup = _.debounce((values: UseForm["values"]) => {
  saveSessionStorageState("messageFormValues", values);
}, 3000);
